'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Shared error boundary page — consumed by every ``error.tsx`` under
 * ``src/app/**``. Before 2026-04-19 there were 11 near-identical
 * copies (~520 LOC total) that diverged on three dimensions only:
 *
 *   * log tag prefix (``[Dashboard Error Boundary]`` vs
 *     ``[Fields Error Boundary]`` vs …)
 *   * "area" description in the copy ("Panel verileri" vs
 *     "Tarla verileri" vs …)
 *   * whether the "Ana Sayfaya Dön" button was rendered
 *
 * One file — ``src/app/fields/error.tsx`` — had drifted to render an
 * inline SVG warning instead of the lucide ``AlertTriangle`` that
 * every other page used, producing a subtly different glyph on one
 * route. Consolidated here.
 *
 * Design unchanged — semantic tokens only, same layout, same copy.
 * Error boundaries re-export this with the right ``area`` label and
 * Next.js' required default export shape.
 */

export interface ErrorBoundaryPageProps {
  /** Standard Next.js error-boundary props — wire these from ``error.tsx``. */
  readonly error: Error & { digest?: string };
  readonly reset: () => void;
  /**
   * Route-specific tag used both in the log prefix and (pluralised) in
   * the fallback copy. Typical values: ``"Dashboard"``, ``"Fields"``,
   * ``"Alarms"``.
   */
  readonly area: string;
  /**
   * Override the default localised description. Usually omitted —
   * the default interpolates ``area`` into "${area} verileri
   * yüklenirken bir hata oluştu." which reads correctly for every
   * existing use.
   */
  readonly description?: string;
  /** When true, show "Ana Sayfaya Dön" linking to /dashboard. */
  readonly withHomeLink?: boolean;
}

export function ErrorBoundaryPage({
  error,
  reset,
  area,
  description,
  withHomeLink = false,
}: ErrorBoundaryPageProps) {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[${area} Error Boundary]`, error);
    }
  }, [area, error]);

  const copy = description ?? 'Beklenmedik bir hata oluştu. Lütfen tekrar deneyin.';

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="flex flex-col items-center gap-2">
        <div className="rounded-full bg-destructive/10 p-4">
          <AlertTriangle className="size-8 text-destructive" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">Bir şeyler ters gitti</h2>
        <p className="max-w-md text-sm text-muted-foreground">{copy}</p>
        {process.env.NODE_ENV === 'development' && error?.message && (
          <pre className="mt-2 max-w-lg overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
            {error.message}
          </pre>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={reset} variant="default" size="sm">
          Tekrar Dene
        </Button>
        {withHomeLink && (
          <Button asChild variant="outline" size="sm">
            <Link href="/dashboard">Ana Sayfaya Dön</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
